import { type ComputedRef, computed } from "vue";
import store from "@/store";
import i18n from "@/i18n";
import {
  getCountryConfig,
  getMarketForInstitution,
  type CountryConfig
} from "@/countries";
import type { TerminologyKey } from "@/countries/types";

export function useCountryConfig(): {
  countryConfig: ComputedRef<CountryConfig>;
  label: (key: TerminologyKey) => string;
  syncLocale: () => string;
} {

  const countryConfig = computed(() => {
    const institution = store.getters["institutions/getInstitution"];
    const market = getMarketForInstitution(institution ?? {});
    return getCountryConfig(market);
  });

  /**
   * Resolves a market-specific terminology concept to its translated string.
   * Use this instead of `isNorwegian ? $t(a) : $t(b)` ternaries in templates.
   */
  function label(key: TerminologyKey): string {
    return i18n.global.t(countryConfig.value.terminology[key]).toString();
  }

  /**
   * Reconciles the active locale with the current market. Called when the user
   * switches institution (and on load when no locale is stored):
   *  - Danish institutions always switch to Danish.
   *  - Norwegian institutions keep the stored locale when it is one the market
   *    offers (nb / nn), otherwise fall back to the market default (nb).
   *
   * The DK / NO behaviour both fall out of `availableLocales`: it is empty for
   * Denmark (so nothing matches and we land on the default `da`) and
   * `["nb", "nn"]` for Norway. The resolved locale is returned so the caller
   * can sync the view and the login service.
   *
   * Storage holds the user's *chosen* locale, not the resolved display locale.
   * A market fallback (e.g. Danish shown to a nynorsk user visiting a DK
   * institution) must never overwrite a stored preference the current market
   * simply doesn't offer — otherwise switching back to Norway would lose the
   * nynorsk choice and revert to the bokmål default. We therefore only persist
   * when seeding storage on first load, or when re-confirming a choice the
   * market actually offers.
   */
  function syncLocale(): string {
    const config = countryConfig.value;
    const user = store.getters["security/getUser"]();
    const userId = user?.id;
    const storageKey = userId ? "locale" + userId : null;
    const storedLocale = storageKey
      ? localStorage.getItem(storageKey)
      : null;

    const resolvedLocale =
      storedLocale && config.availableLocales.includes(storedLocale)
        ? storedLocale
        : config.locale;

    (i18n.global.locale as unknown as { value: string }).value =
      resolvedLocale;

    // `resolvedLocale` falls back to the market default whenever the stored
    // choice isn't offered here. Persisting that fallback would overwrite the
    // user's real preference (e.g. nynorsk while visiting a DK institution),
    // so only persist when seeding on first load or re-confirming an offered
    // choice — never the fallback itself.
    const isUnsupportedFallback =
      !!storedLocale && !config.availableLocales.includes(storedLocale);
    if (storageKey && !isUnsupportedFallback) {
      localStorage.setItem(storageKey, resolvedLocale);
    }
    return resolvedLocale;
  }

  return { countryConfig, label, syncLocale };
}
